export const CURRENT_SHA = "3f2a"
export const STALE_SHA = "a91e"

export type WireGroup = "AB" | "B" | "C" | "D"
export type WireBranch = "AB" | "B" | "C0" | "C1" | "C2" | "D"
export type WireDirection = "up" | "down"

export interface WireState {
  on: boolean
  dir?: WireDirection
  label?: string | null
  /** Lights only these branches of a fan instead of all of them. */
  only?: WireBranch[]
}

export interface ReplicaState {
  sha: string
  status: string | null
}

export interface SpokesPhase {
  title: string
  caption: string
  fetchSha: string
  spokesStatus: string | null
  replicas: [ReplicaState, ReplicaState, ReplicaState]
  /** Index of the replica the proxy picked to serve the read. */
  highlight: number | null
  wires: Record<WireGroup, WireState>
}

const quorum = "3 of 3 match · strict majority"
const off: WireState = { on: false }
const settled = (sha: string): ReplicaState => ({ sha, status: null })

export const spokesPhases: SpokesPhase[] = [
  {
    title: "Pack and stream the push",
    caption: "The client sends its pack to the Spokes Proxy, which streams it to every replica.",
    fetchSha: STALE_SHA,
    spokesStatus: null,
    replicas: [
      { sha: STALE_SHA, status: "voting" },
      { sha: STALE_SHA, status: "voting" },
      { sha: STALE_SHA, status: "voting" },
    ],
    highlight: null,
    wires: {
      AB: { on: true, dir: "down", label: "pack" },
      B: off,
      C: { on: true, dir: "down", label: "stream pack" },
      D: off,
    },
  },
  {
    title: "Replicas stage the pack",
    caption: "Each replica stages the incoming pack before applying it to its own copy of main.",
    fetchSha: STALE_SHA,
    spokesStatus: null,
    replicas: [
      { sha: STALE_SHA, status: "staging" },
      { sha: STALE_SHA, status: "staging" },
      { sha: STALE_SHA, status: "staging" },
    ],
    highlight: null,
    wires: { AB: off, B: off, C: { on: true, dir: "down", label: null }, D: off },
  },
  {
    title: "Majority quorum reached",
    caption: "All three replicas report back — a strict majority quorum is reached on 3f2a.",
    fetchSha: STALE_SHA,
    spokesStatus: quorum,
    replicas: [
      { sha: CURRENT_SHA, status: "matches" },
      { sha: CURRENT_SHA, status: "matches" },
      { sha: CURRENT_SHA, status: "matches" },
    ],
    highlight: null,
    wires: { AB: off, B: off, C: { on: true, dir: "up", label: null }, D: off },
  },
  {
    title: "Acknowledge the push",
    caption: "The proxy acknowledges the push, and the replicas settle on the new commit.",
    fetchSha: STALE_SHA,
    spokesStatus: quorum,
    replicas: [settled(CURRENT_SHA), settled(CURRENT_SHA), settled(CURRENT_SHA)],
    highlight: null,
    wires: {
      AB: { on: true, dir: "up", label: "ack" },
      B: off,
      C: { on: true, dir: "up", label: null },
      D: off,
    },
  },
  {
    title: "A separate fetch arrives",
    caption: "A second client fetches main — still pointing at the old commit, a91e.",
    fetchSha: STALE_SHA,
    spokesStatus: quorum,
    replicas: [settled(CURRENT_SHA), settled(CURRENT_SHA), settled(CURRENT_SHA)],
    highlight: null,
    wires: {
      AB: off,
      B: { on: true, dir: "down", label: "fetch" },
      C: { on: true, dir: "up", label: null },
      D: off,
    },
  },
  {
    title: "Choose a current replica",
    caption: "The proxy chooses a current replica to serve the read — replica 02, up to date.",
    fetchSha: STALE_SHA,
    spokesStatus: quorum,
    replicas: [settled(CURRENT_SHA), settled(CURRENT_SHA), settled(CURRENT_SHA)],
    highlight: 1,
    wires: {
      AB: off,
      B: off,
      C: { on: true, dir: "down", label: "choose current replica", only: ["C1"] },
      D: off,
    },
  },
  {
    title: "Return the new tip",
    caption: "The chosen replica returns its ref and pack. The client's fetch catches up to 3f2a.",
    fetchSha: CURRENT_SHA,
    spokesStatus: quorum,
    replicas: [settled(CURRENT_SHA), settled(CURRENT_SHA), settled(CURRENT_SHA)],
    highlight: 1,
    wires: {
      AB: off,
      B: { on: true, dir: "up", label: "pack + tip" },
      C: off,
      D: { on: true, dir: "up", label: "ref + pack" },
    },
  },
]

/**
 * What the scene shows before the first step plays: every replica on the old
 * tip, nothing in flight. Stands in for "the step before 01" on the first pass
 * only, so the opening step types its statuses in instead of flipping back
 * from step 07.
 */
export const spokesInitialPhase: SpokesPhase = {
  ...spokesPhases[0]!,
  replicas: [settled(STALE_SHA), settled(STALE_SHA), settled(STALE_SHA)],
  wires: { AB: off, B: off, C: off, D: off },
}
